import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import { isFirstChargeAlertDue, storeManageUrl } from "../lib/billing";

export interface Subscription {
  id: string;
  user_id: string;
  stripe_customer_id: string;
  stripe_subscription_id: string;
  status: string;
  plan: "monthly" | "yearly";
  price_id: string;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  created_at: string;
  updated_at: string;
  /** Missing until the Play columns are applied. Stripe rows stay "stripe". */
  provider?: "stripe" | "google" | "apple" | null;
}

export type PaymentFix = "stripe" | "play" | "apple";

interface UseSubscriptionReturn {
  isPro: boolean;
  /** Trial, paid month, or an owner account that does not pay. */
  hasAccess: boolean;
  /** This account already used its one trial. */
  hasUsedTrial: boolean;
  /** The last charge failed and the plan is still open, including a pause. */
  paymentFailed: boolean;
  /** Where a failed charge is fixed. Play stays on the existing Google plan. */
  paymentFix: PaymentFix;
  /** The plan could not be read. Do not treat that as a new trial. */
  subscriptionLoadFailed: boolean;
  /** Last 3 days before the first charge, until they quit. */
  showFirstChargeAlert: boolean;
  isLoading: boolean;
  subscription: Subscription | null;
  plan: "free" | "monthly" | "yearly" | "included";
  createCheckoutSession: (priceId: string) => Promise<string | null>;
  createPortalSession: () => Promise<string | null>;
  refetch: () => Promise<void>;
}

function grantsAccess(status: string): boolean {
  return status === "active" || status === "trialing";
}

/** The plan is still open and must not start a second checkout. */
function paymentIsStillOpen(status: string): boolean {
  return (
    status === "past_due" ||
    status === "unpaid" ||
    status === "incomplete" ||
    status === "paused"
  );
}

function paymentFixFor(latest: Subscription | null): PaymentFix {
  if (!latest || !paymentIsStillOpen(latest.status)) return "stripe";
  if (latest.provider === "google") return "play";
  if (latest.provider === "apple") return "apple";
  return "stripe";
}

interface BillingLoad {
  phase: "loading" | "ready" | "error";
  /** Newest subscription row, including one that does not open the app. */
  latest: Subscription | null;
  exempt: boolean;
}

const signedOut: BillingLoad = { phase: "ready", latest: null, exempt: false };

function isExemptRow(result: {
  data: unknown;
  error: { code?: string; message?: string } | null;
}): boolean {
  // The table arrives with the monthly-plan migration. Until then a missing
  // table means "not exempt", not a crash.
  const missing =
    result.error?.code === "PGRST205" || result.error?.code === "42P01";
  if (result.error && !missing) {
    console.error("Error fetching billing exemption:", result.error);
  }
  return Array.isArray(result.data) && result.data.length > 0 && !result.error;
}

export function useSubscription(): UseSubscriptionReturn {
  const { user } = useAuth();
  const [load, setLoad] = useState<BillingLoad>({
    phase: "loading",
    latest: null,
    exempt: false,
  });

  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setLoad(signedOut);
      return;
    }

    setLoad((prev) => ({ ...prev, phase: "loading" }));
    try {
      const [subscriptionResult, exemptionResult] = await Promise.all([
        supabase
          .from("subscriptions")
          .select("*")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from("billing_exemptions")
          .select("user_id")
          .eq("user_id", user.id)
          .limit(1),
      ]);

      const exempt = isExemptRow(exemptionResult);
      if (subscriptionResult.error) {
        console.error("Error fetching subscription:", subscriptionResult.error);
        setLoad((prev) => ({ ...prev, phase: "error", exempt }));
        return;
      }

      setLoad({
        phase: "ready",
        latest: (subscriptionResult.data ?? null) as Subscription | null,
        exempt,
      });
    } catch (err) {
      console.error("Error fetching subscription:", err);
      setLoad((prev) => ({ ...prev, phase: "error" }));
    }
  }, [user]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const open =
    load.latest && grantsAccess(load.latest.status) ? load.latest : null;
  const hasPaidAccess = open !== null;
  const hasAccess = load.exempt || hasPaidAccess;
  const isPro = hasAccess;
  const plan: "free" | "monthly" | "yearly" | "included" = hasPaidAccess
    ? open.plan
    : load.exempt
      ? "included"
      : "free";
  const showFirstChargeAlert = open
    ? isFirstChargeAlertDue(
        open.status,
        open.current_period_end,
        open.cancel_at_period_end,
        new Date(),
      )
    : false;

  const createCheckoutSession = useCallback(
    async (priceId: string): Promise<string | null> => {
      try {
        const { data, error } = await supabase.functions.invoke(
          "create-checkout-session",
          {
            body: {
              priceId,
              successUrl: `${window.location.origin}/?checkout=success`,
              cancelUrl: `${window.location.origin}/?checkout=canceled`,
            },
          },
        );

        if (error) {
          console.error("Error creating checkout session:", error);
          return null;
        }

        return data?.url || null;
      } catch (err) {
        console.error("Error creating checkout session:", err);
        return null;
      }
    },
    [],
  );

  const createPortalSession = useCallback(async (): Promise<string | null> => {
    const storeUrl = storeManageUrl(load.latest?.provider);
    if (storeUrl) return storeUrl;
    try {
      const { data, error } = await supabase.functions.invoke(
        "create-portal-session",
        {
          body: {
            returnUrl: window.location.origin,
          },
        },
      );

      if (error) {
        console.error("Error creating portal session:", error);
        return null;
      }

      return data?.url || null;
    } catch (err) {
      console.error("Error creating portal session:", err);
      return null;
    }
  }, [load.latest]);

  return {
    isPro,
    hasAccess,
    hasUsedTrial: load.latest !== null,
    paymentFailed: load.latest ? paymentIsStillOpen(load.latest.status) : false,
    paymentFix: paymentFixFor(load.latest),
    subscriptionLoadFailed: load.phase === "error",
    showFirstChargeAlert,
    isLoading: load.phase === "loading",
    subscription: open,
    plan,
    createCheckoutSession,
    createPortalSession,
    refetch: fetchSubscription,
  };
}
